import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import TestVehicleApi from '../../../components/TestVehicleApi';
import VehicleApiDebug from '../../../components/VehicleApiDebug';

const VehicleApiTest = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authStatus, setAuthStatus] = useState({
    token: localStorage.getItem('token') ? 'Present' : 'Missing',
    user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching vehicles...');
      console.log('Current token:', localStorage.getItem('token'));
      
      const response = await api.get('/api/v1/admin/vehicles');
      
      console.log('API Response:', response);
      setVehicles(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError(err.message || 'Failed to fetch vehicles');
      setLoading(false);
    }
  };

  const testDirectFetch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:8000/api/v1/admin/vehicles', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetch API Response:', data);
      setVehicles(data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error with direct fetch:', err);
      setError(err.message || 'Failed to fetch vehicles');
      setLoading(false);
    }
  };

  const testLogin = async () => {
    try {
      const response = await api.post('/auth/login', {
        email: 'admin@example.com',
        password: 'password',
        isAdmin: true
      });
      
      console.log('Login response:', response.data);
      
      if (response.data.success) {
        const { token, user } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        setAuthStatus({
          token: 'Present (New)',
          user: user
        });
        
        // Fetch vehicles with new token
        fetchVehicles();
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Vehicle API Test</h1>
      
      {/* New VehicleApiDebug component */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Advanced API Debug Tool</h2>
        <VehicleApiDebug />
      </div>
      
      {/* New TestVehicleApi component */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">API Test Tool</h2>
        <TestVehicleApi />
      </div>
      
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">Authentication Status</h2>
        <div className="grid grid-cols-2 gap-2">
          <div className="font-medium">Token:</div>
          <div className={authStatus.token === 'Missing' ? 'text-red-500' : 'text-green-500'}>
            {authStatus.token}
          </div>
          
          <div className="font-medium">User:</div>
          <div>
            {authStatus.user ? (
              <span className="text-green-500">
                {authStatus.user.name} ({authStatus.user.email})
                {authStatus.user.role && ` - Role: ${authStatus.user.role}`}
              </span>
            ) : (
              <span className="text-red-500">Not logged in</span>
            )}
          </div>
        </div>
        
        {!authStatus.token || authStatus.token === 'Missing' ? (
          <button
            onClick={testLogin}
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Login as Admin
          </button>
        ) : null}
      </div>
      
      <div className="flex space-x-4 mb-6">
        <button
          onClick={fetchVehicles}
          disabled={loading}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Test API with Axios'}
        </button>
        
        <button
          onClick={testDirectFetch}
          disabled={loading}
          className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Test API with Fetch'}
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Vehicles from Database</h2>
          <p className="text-gray-600 text-sm">
            {loading ? 'Loading...' : `Found ${vehicles.length} vehicles`}
          </p>
        </div>
        
        {vehicles.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capacity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.map(vehicle => (
                  <tr key={vehicle.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {vehicle.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.registration_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.capacity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        vehicle.status === 'active' ? 'bg-green-100 text-green-800' : 
                        vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {vehicle.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !loading && (
          <div className="p-4 text-center text-gray-500">
            No vehicles found in the database.
          </div>
        )}
      </div>
      
      <div className="mt-6 bg-gray-100 p-4 rounded-lg">
        <h2 className="text-lg font-semibold mb-2">API Request Details</h2>
        <p className="text-sm text-gray-600 mb-2">
          Open your browser's developer console to see detailed API request and response logs.
        </p>
        <pre className="bg-gray-800 text-green-400 p-4 rounded text-xs overflow-auto max-h-60">
          {`GET http://localhost:8000/api/v1/admin/vehicles
Headers:
- Authorization: Bearer [your-token]
- Accept: application/json

Expected Response:
{
  "success": true,
  "data": [
    {
      "id": 1,
      "name": "Toyota Land Cruiser",
      "registration_number": "KCB 123A",
      "type": "SUV",
      "capacity": 7,
      "status": "active",
      ...
    },
    ...
  ],
  "message": "Vehicles retrieved successfully"
}`}
        </pre>
      </div>
    </div>
  );
};

export default VehicleApiTest;
